import Vue from 'vue';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import API from '@/lib/API';
import { isRememberableView } from '@/helpers/lastView';
import vuetify from '@/plugins/vuetify';
import {
  User, PermissionCode, Credentials,
  Network, UserSettings,
} from '@/types';

const defaultNetwork: Network = {
  id: 0,
  name: 'Syndesia',
  shortname: 'syndesia',
  title: 'SYNDESK',
  // Logo mostrato nella pagina di login: versione orizzontale a colori, con il payoff.
  // Login.vue mostra un fallback testuale (il titolo del portale) quando logo è null.
  // L'app bar usa invece la versione bianca senza payoff (vedi App.vue).
  logo: 'syndesia-logo.svg',
  primaryColor: '#212F50',
};

// Cerca di ottenere le reti di vendita / brand dell'utente
function getUserNetworks(user: User): Network[] {
  const networks: Network[] = [];

  if (user && Array.isArray(user.groups) && user.groups.length > 0) {
    user.groups.forEach((group) => {
      if (Array.isArray(group.networks) && group.networks.length > 0) {
        // Aggiunge il network alla lista solo se non è già presente
        group.networks.forEach((n) => {
          if (!networks.find((x) => x.id === n.id)) {
            networks.push(n);
          }
        });
      }
    });
  }

  return networks;
}

// Cerca di ottenere la rete di vendita / brand principale dell'utente
function getUserNetwork(user: User): Network {
  const networks = getUserNetworks(user);

  // Se l'utente ha più di una rete di vendita / brand, restituisce la prima
  if (networks.length > 0) {
    return networks[0];
  }

  // Altrimenti restituisce il brand di default
  return defaultNetwork;
}

@Module
export default class Auth extends VuexModule {
  // State

  // L'utente autenticato
  user: User | null = null

  // L'elenco dei permessi dell'utente
  userPermissions: PermissionCode[] | null = null

  // L'elenco delle reti di vendita / brand dell'utente
  userNetworks: Network[] = []

  // La rete di vendita / brand dell'utente principale
  userNetwork: Network | null = null

  // Getters

  get currentUser(): User | null {
    return this.user;
  }

  get currentUserPermissions(): PermissionCode[] {
    return this.userPermissions ? this.userPermissions : [];
  }

  // L'ultima vista usata. Sta nel database se l'utente ha acceso "memorizza
  // impostazioni", altrimenti solo in sessione: e' lo stesso bivio dei filtri.
  get lastView(): string | null {
    if (!this.user) return null;

    if (this.user.permanentSettings === true) {
      // `settings` e' un blob JSON condiviso con i filtri: se e' rovinato non
      // deve portarsi dietro la navigazione, che da qui passa sempre.
      try {
        const settings: UserSettings = this.user.settings ? JSON.parse(this.user.settings) : {};
        return settings.lastView ?? null;
      } catch (error) {
        return null;
      }
    }

    return Vue.prototype.$session.get('lastView') ?? null;
  }

  get currentUserNetworks(): Network[] {
    return this.userNetworks;
  }

  get currentUserNetwork(): Network | null {
    return this.userNetwork;
  }

  get primaryColor(): string {
    return this.userNetwork?.primaryColor
      ? this.userNetwork.primaryColor
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      : defaultNetwork.primaryColor!;
  }

  get logo(): string | null {
    return this.userNetwork?.logo
      ? this.userNetwork.logo
      : defaultNetwork.logo;
  }

  get title(): string {
    return this.userNetwork?.title
      ? this.userNetwork.title
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      : defaultNetwork.title!;
  }

  @Mutation
  setUser({ user }: { user: User}) {
    // console.log('mutations:setUser'); // eslint-disable-line no-console

    // Memorizza l'utente (o lo cancella se null)
    this.user = user;
  }

  @Mutation
  setUserSettings(settings: string) {
    // Aggiorna la copia locale del blob: `fetchUser` non viene richiamata a
    // ogni navigazione, e senza questo il confronto in `saveLastView`
    // rifarebbe la stessa PUT a ogni cambio vista.
    if (this.user) this.user.settings = settings;
  }

  @Mutation
  setUserPermissions({ user }: { user: User}) {
    // console.log('mutations:setUserPermissions'); // eslint-disable-line no-console

    // Memorizza l'elenco dei permessi dell'utente
    if (user && Array.isArray(user.Permissions)) {
      const up: PermissionCode[] = user.Permissions.map((p) => p.codename);
      const gp: PermissionCode[] = [];

      if (Array.isArray(user.groups) && user.groups.length > 0) {
        user.groups.forEach((group) => {
          if (Array.isArray(group.Permissions) && group.Permissions.length > 0) {
            gp.push(...group.Permissions.map((p) => p.codename));
          }
        });
      }

      this.userPermissions = [...up, ...gp];
    } else {
      this.userPermissions = null;
    }
  }

  @Mutation
  setUserNetwork({ network }: { network: Network }) {
    console.log(`mutations:setUserNetwork(${network.shortname})`); // eslint-disable-line no-console

    this.userNetwork = {
      id: network.id,
      name: network.name,
      shortname: network.shortname,
      title: network.title,
      logo: network.logo,
      primaryColor: network.primaryColor,
    };
  }

  @Mutation
  setUserNetworks({ networks }: { networks: Network[] }) {
    console.log('mutations:setUserNetworks() networks =', networks); // eslint-disable-line no-console

    this.userNetworks = networks.map((n) => ({
      id: n.id,
      name: n.name,
      shortname: n.shortname,
      title: n.title,
      logo: n.logo,
      primaryColor: n.primaryColor,
    }));
  }

  @Action({ rawError: true })
  async fetchUser() {
    // console.log('actions:fetchUser'); // eslint-disable-line no-console

    const user = await API.fetchUser();

    // console.log('actions:fetchUser user =', user); // eslint-disable-line no-console

    if (user instanceof Error) {
      this.context.commit('setUser', { user: null });
      this.context.commit('setUserPermissions', { user: null });
      return Promise.reject(user);
    }

    // Memorizza le reti di vendita / brand dell'utente
    const networks = getUserNetworks(user);
    const network = getUserNetwork(user);
    this.context.commit('setUserNetworks', { networks });
    this.context.commit('setUserNetwork', { network });

    this.context.commit('setUser', { user });
    this.context.commit('setUserPermissions', { user });
    return user;
  }

  @Action({ rawError: true })
  async login({ username, password }: Credentials) {
    console.log('actions:login'); // eslint-disable-line no-console

    const result = await API.login({ username, password });

    console.log('actions:login result =', result); // eslint-disable-line no-console

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    // Recupere le informazioni dell'utente autenticato
    this.context.dispatch('fetchUser');

    return result;
  }

  @Action({ rawError: true })
  async logout() {
    console.log('actions:logout'); // eslint-disable-line no-console

    this.context.commit('setUser', { user: null });
    this.context.commit('setUserPermissions', { user: null });
    this.context.commit('setUserNetwork', { network: defaultNetwork });
    this.context.commit('setUserNetworks', { networks: [] });

    const result = await API.logout();

    console.log('actions:logout result =', result); // eslint-disable-line no-console

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true })
  async updateLastActivity() {
    console.log('actions:updateLastActivity'); // eslint-disable-line no-console

    const result = await API.updateLastActivity();

    console.log('actions:updateLastActivity result =', result); // eslint-disable-line no-console

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // Applica la rete di vendita / brand specificato
  @Action({ rawError: true })
  async updateUserNetwork({ shortname }: { shortname: string }) {
    console.log(`actions:updateUserNetwork(${shortname})`); // eslint-disable-line no-console

    // Ottiene dallo store l'elenco completo delle reti di vendita / brand
    const networks = await API.getNetworks();
    // const { networks } = this.context.rootGetters;
    console.log(`actions:updateUserNetwork(${shortname}) networks =`, networks); // eslint-disable-line no-console

    // Se il brand specificato è uno di quelli supportati, lo imposta
    // altrimnenti imposta il brand di default.
    const network = networks.find((n) => n.shortname === shortname) ?? defaultNetwork;

    if (network?.primaryColor) {
      // ...imposta il colore dell'interfaccia con quello del brand
      vuetify.framework.theme.themes.light.primary = network.primaryColor;
      // e memorizza il brand selezionato.
      this.context.commit('setUserNetwork', { network });
    }
  }

  // Memorizza la vista su cui riaprire. Chiamata a ogni navigazione, quindi
  // non scrive se il valore non e' cambiato.
  @Action({ rawError: true })
  async saveLastView(path: string) {
    if (!isRememberableView(path)) return;

    const user = this.context.getters.currentUser as User | null;
    if (!user) return;
    if (this.context.getters.lastView === path) return;

    if (user.permanentSettings === true) {
      let settings: UserSettings = {};
      try {
        settings = user.settings ? JSON.parse(user.settings) : {};
      } catch (error) {
        settings = {};
      }
      settings.lastView = path;

      this.context.commit('setUserSettings', JSON.stringify(settings));
      await API.updateLastView({ lastView: path });
    } else {
      Vue.prototype.$session.set('lastView', path);
    }
  }
}
